import React, { useState, useEffect } from 'react';
import { Bell, Film } from 'lucide-react';
import { supabase } from '../../../supabaseClient';

interface NotificationItem {
  id: string;
  title: string;
  thumbnail?: string;
  created_at: string;
  category?: string;
  post_type?: string;
  is_short?: boolean;
}

export default function NotificationsDropdown({ onSelectVideo }: { onSelectVideo?: (videoId: string) => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = async () => {
    setLoading(true);
    
    const seteDiasAtras = new Date();
    seteDiasAtras.setDate(seteDiasAtras.getDate() - 7);

    // 🛡️ ADICIONADO post_type e is_short na busca para identificar shorts nas notificações
    const { data, error } = await supabase
      .from('videos')
      .select('id, title, thumbnail, created_at, category, post_type, is_short')
      .gt('created_at', seteDiasAtras.toISOString())
      .order('created_at', { ascending: false })
      .limit(10);

    if (error) {
      console.error('Erro ao buscar notificações:', error);
    } else if (data) {
      const readIds = JSON.parse(localStorage.getItem('doisplay_read_notifications') || '[]');
      const unread = data.filter((item: any) => !readIds.includes(item.id));
      setNotifications(unread);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

 const handleNotificationClick = (item: NotificationItem) => {
    const readIds = JSON.parse(localStorage.getItem('doisplay_read_notifications') || '[]');
    if (!readIds.includes(item.id)) {
      readIds.push(item.id);
      localStorage.setItem('doisplay_read_notifications', JSON.stringify(readIds));
    }

    setNotifications(prev => prev.filter(n => n.id !== item.id));
    setIsOpen(false);

    // 1. Verifica se é Short
    const isShortVideo = item.post_type === 'shorts' || item.is_short === true || (item.category || '').toLowerCase().includes('shorts');
    if (isShortVideo) {
      window.location.href = `/shorts?v=${item.id}`; 
      return;
    }

    // 2. Verifica se é do menu PlayFeed (vídeos publicados pelo utilizador)
    const isFeedVideo = item.post_type === 'feed' || item.post_type === 'app_movel';
    if (isFeedVideo) {
      window.location.href = `/playfeed?v=${item.id}`;
      return;
    }

    // 3. Se não for short nem feed, segue o fluxo normal (abre o vídeo de filme/game no player)
    if (onSelectVideo) {
      onSelectVideo(item.id);
    }
  };

  return (
    // 🛡️ LIGADO NOVAMENTE: Volta a exibir no PC e Celular
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <div 
        onClick={() => setIsOpen(!isOpen)} 
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', position: 'relative' }}
      >
        <Bell size={22} color="#fff" />
        
        {notifications.length > 0 && (
          <span style={{ 
            position: 'absolute', top: '-6px', right: '-8px', 
            backgroundColor: '#E50914', color: '#fff', fontSize: '10px', 
            fontWeight: 'bold', padding: '1px 5px', borderRadius: '10px',
            minWidth: '15px', textAlign: 'center'
          }}>
            {notifications.length}
          </span>
        )}
      </div>

      {isOpen && (
        <div style={{ 
          position: 'absolute', right: 0, top: '40px', width: '320px', 
          backgroundColor: '#181818', border: '1px solid #333', borderRadius: '6px', 
          padding: '15px', boxShadow: '0px 8px 16px rgba(0,0,0,0.8)', zIndex: 1100,
          maxHeight: '400px', overflowY: 'auto'
        }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#fff', borderBottom: '1px solid #333', paddingBottom: '8px' }}>
            Notificações
          </h4>

          {loading ? (
            <p style={{ margin: 0, fontSize: '12px', color: '#888', textAlign: 'center', padding: '10px 0' }}>Carregando...</p>
          ) : notifications.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {notifications.map((item) => {
                const isShortNotification = item.post_type === 'shorts' || item.is_short === true || (item.category || '').toLowerCase().includes('shorts');
                const isSerieOrCanal = item.category?.toLowerCase().includes('serie') || item.category?.toLowerCase().includes('canais');

                return (
                  <div 
                    key={item.id} 
                    onClick={() => handleNotificationClick(item)}
                    style={{ 
                      display: 'flex', gap: '10px', alignItems: 'center',
                      borderBottom: '1px solid rgba(255,255,255,0.05)', 
                      paddingBottom: '8px', cursor: 'pointer',
                      transition: 'background 0.2s', borderRadius: '4px', padding: '6px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    {item.thumbnail ? (
                      <img 
                        src={item.thumbnail} 
                        alt="Capa" 
                        style={{ width: '56px', height: '32px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} 
                      />
                    ) : (
                      <div style={{ width: '56px', height: '32px', backgroundColor: '#222', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Film size={16} color="#666" />
                      </div>
                    )}

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: '0 0 2px 0', fontSize: '12px', fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {item.title}
                      </p>
                      <p style={{ margin: '0 0 2px 0', fontSize: '11px', color: '#ccc', lineHeight: '1.2' }}>
                        {isShortNotification ? "Novo Short publicado! ⚡" : isSerieOrCanal ? "Novo episódio disponível 📺" : "Novo conteúdo disponível 🎬"}
                      </p>
                      <span style={{ fontSize: '9px', color: '#777' }}>
                        {new Date(item.created_at).toLocaleDateString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: '12px', color: '#aaa', textAlign: 'center', padding: '10px 0' }}>
              Nenhuma nova notificação no momento.
            </p>
          )}
        </div>
      )}
    </div>
  );
}